import axios, { AxiosError, type AxiosInstance } from "axios";

// In dev, Vite proxies /admin and /v1 to Spring on :8080.
// In prod, the SPA is served by Spring from classpath so same-origin works out of the box.
// Either way, no base URL is needed — requests are same-origin.

function readCsrfFromCookie(): string | null {
    const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
}

export const api: AxiosInstance = axios.create({
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Attach CSRF header on every mutation.
api.interceptors.request.use((config) => {
    const method = (config.method || "get").toLowerCase();
    if (method !== "get" && method !== "head" && method !== "options") {
        const token = readCsrfFromCookie();
        if (token) {
            config.headers.set("X-XSRF-TOKEN", token);
        }
    }
    return config;
});

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
    onUnauthorized = handler;
}

api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401 && onUnauthorized) {
            onUnauthorized();
        }
        return Promise.reject(error);
    }
);

// Helper: extract a friendly error message from an Axios error.
export function apiErrorMessage(error: unknown, fallback = "Something went wrong"): string {
    if (error instanceof AxiosError) {
        const data = error.response?.data as { error?: string; message?: string; details?: string[] } | undefined;
        if (data?.details && data.details.length > 0) {
            return data.details.join("; ");
        }
        if (data?.error) return data.error;
        if (data?.message) return data.message;
        if (error.message) return error.message;
    }
    return fallback;
}
